import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "Styles/GerminationTray.sass";
import "react-calendar/dist/Calendar.css";

import PlantingCard from "Components/PlantingCard";
import TrashIcon from "Assets/TrashIcon";
import MultiSelectIcon from "Assets/MultiSelectIcon";
import Modal from "react-modal";
import { ModalStyle } from "Assets/ModalStyle";

import DeleteGerminationTrayModal from "Components/DeleteGerminationTrayModal";
import { usePlantStore } from "Store/PlantStore";
import { usePlantingStore } from "Store/PlantingStore";
import { useGerminationTrayStore } from "Store/GerminationTrayStore";
import { useVarietyStore } from "Store/VarietyStore";
import { getPlantDisplayName } from "Helpers/PlantName";
import type PlantingModel from "Models/Planting";

const GerminationTray = () => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedPlantings, setSelectedPlantings] = useState<string[]>([]);
  const [showDeletePlantingsModal, setShowDeletePlantingsModal] =
    useState(false);

  const plants = usePlantStore((state) => state.fetchPlants);
  const plantModels = usePlantStore((state) => state.plants);
  const plantings = usePlantingStore((state) => state.plantings);
  const fetchPlantings = usePlantingStore((state) => state.getPlantings);
  const deletePlanting = usePlantingStore((state) => state.deletePlanting);
  const fetchTray = useGerminationTrayStore(
    (state) => state.getGerminationTray,
  );
  const updatePlantingAtIndex = useGerminationTrayStore(
    (state) => state.updatePlantingAtIndex,
  );
  const fetchVarieties = useVarietyStore((state) => state.fetchVarieties);
  const varieties = useVarietyStore((state) => state.varieties);
  const germinationTray = useGerminationTrayStore(
    (state) => state.currentGerminationTray,
  );
  const { id } = useParams();

  const loadTrayData = useCallback(async () => {
    if (!id) return;

    const trayData = await fetchTray(id);
    const plantingIds = trayData.plantingIds.filter(
      (plantingId): plantingId is string => plantingId !== null,
    );
    if (plantingIds.length) {
      await fetchPlantings(plantingIds);
    }
  }, [fetchPlantings, fetchTray, id]);

  useEffect(() => {
    void loadTrayData();
    void plants();
    void fetchVarieties();
  }, [loadTrayData, plants, fetchVarieties]);

  const deleteSelectedPlantings = async () => {
    const tray = useGerminationTrayStore.getState().currentGerminationTray;
    if (!tray) {
      throw new Error(
        "Cannot delete plantings: germination tray is unavailable",
      );
    }

    for (const plantingId of selectedPlantings) {
      await deletePlanting(plantingId);
      const indexes = tray.plantingIds.reduce<number[]>(
        (result, currentId, index) => {
          if (currentId === plantingId) {
            result.push(index);
          }
          return result;
        },
        [],
      );
      for (const index of indexes) {
        await updatePlantingAtIndex(index);
      }
    }

    const updatedTray =
      useGerminationTrayStore.getState().currentGerminationTray;
    if (updatedTray) {
      const remainingPlantingIds = updatedTray.plantingIds.filter(
        (plantingId): plantingId is string => plantingId !== null,
      );
      await fetchPlantings(remainingPlantingIds);
    }

    setSelectedPlantings([]);
    setMultiSelectMode(false);
    setShowDeletePlantingsModal(false);
  };

  const selectedPlantingModels = selectedPlantings
    .map((plantingId) =>
      plantings.find((planting) => planting._id === plantingId),
    )
    .filter((planting): planting is PlantingModel => planting !== undefined);

  if (!germinationTray || germinationTray._id !== id) return null;

  return (
    <div>
      <div className="flex py-5">
        <h2 className="text-4xl text-center my-0 mx-auto">
          {germinationTray.name}
        </h2>
        <div className="flex mr-[30px] items-center">
          {multiSelectMode && selectedPlantings.length > 0 && (
            <button
              aria-label="Delete selected plantings"
              className="w-10 h-10 text-red"
              onClick={() => setShowDeletePlantingsModal(true)}
            >
              <TrashIcon />
            </button>
          )}

          <button
            aria-label="Toggle multiselect mode"
            className="w-10 h-10"
            onClick={() => {
              setMultiSelectMode(!multiSelectMode);
              if (!multiSelectMode) {
                setSelectedPlantings([]);
              }
            }}
          >
            <MultiSelectIcon
              stroke={multiSelectMode ? "rgb(28, 151, 246)" : "#000"}
            />
          </button>
        </div>
        <button
          onClick={() => setShowDeleteModal(true)}
          className="btn p-15 text-[red] bg-[red] circle mr-5"
        >
          <TrashIcon />
        </button>
      </div>

      <div className="grid-container-parent">
        <div
          className="grid-container"
          style={{
            gridTemplateColumns: `repeat(${germinationTray.width}, minmax(90px, 1fr))`,
            gridTemplateRows: `repeat(${germinationTray.height}, minmax(90px, 1fr))`,
          }}
        >
          {germinationTray.plantingIds.map((plantingId, i) => {
            return (
              <div key={i}>
                <PlantingCard
                  index={i}
                  planting={plantings.find((p) => p._id === plantingId)}
                  multiSelectMode={multiSelectMode}
                  selectedPlantings={selectedPlantings}
                  setSelectedPlantings={setSelectedPlantings}
                />
              </div>
            );
          })}
        </div>
      </div>

      <DeleteGerminationTrayModal
        trayId={germinationTray._id}
        setShowDeleteModal={setShowDeleteModal}
        showDeleteModal={showDeleteModal}
        redirectToTrays={true}
      />

      <Modal
        style={ModalStyle.defaultStyle}
        ariaHideApp={false}
        isOpen={showDeletePlantingsModal}
      >
        <div className="m-50 p-5 grid grid-cols-1 h-full w-full text-center justify-center items-center">
          <h3 className="text-lg py-5">Delete Plantings</h3>
          <p>
            Are you sure you want to delete the following{" "}
            {selectedPlantings.length} plantings?
          </p>
          <div className="flex items-center justify-center">
            {selectedPlantingModels.map((planting) => {
              const plant = plantModels.find(
                (item) => item._id === planting.plantId,
              );
              return (
                <p className="p-2" key={planting._id}>
                  {getPlantDisplayName(plant, varieties) || "Unknown Plant"}
                </p>
              );
            })}
          </div>
          <div className="flex justify-center items-center">
            <button
              onClick={() => setShowDeletePlantingsModal(false)}
              className="btn bg-gray-light mr-5 h-10 w-[150px]"
            >
              Cancel
            </button>
            <button
              className="btn bg-red text-white mr-5 h-10 w-[150px]"
              onClick={() => void deleteSelectedPlantings()}
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default GerminationTray;
